import { Box, Grid, TableCell, TableRow, Typography } from "@mui/material";
import React, { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import PageTitle from "../../../../components/pageTitle/PageTitle";
import TableDesign, {
  TableHeading,
} from "../../../../components/common/table/TableDesign";
import { useSelector } from "react-redux";
import {
  getAllOperationStaffAction,
  getAllSkillAction,
  setAddOperationStaffAction,
  setDeleteOperationStaffAction,
} from "../../../../store/reducers/assignWorkerSlice";
import ManageStaff from "./ManageStaff";
import { STAFF_FILTERS } from "./staffAllFields";
import FormField from "../../../../components/common/FormField";

interface FilterOption {
  page: number;
  limit: number;
  orderBy: string;
  orderIn: string;
  search: string;
  skill: any;
  is_deleted?: string;
  [key: string]: any;
}

interface StaffListProps {
  setSelectedId?: any;
  selectedId?: any;
}

const ROW_HEIGHT = 60; // px, adjust to your row/card height

const StaffList: React.FC<StaffListProps> = ({ setSelectedId, selectedId }) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false); // State variable to manage dialog open/close
  const {
    skills,
    addOperationStaffResponse,
    operationStaffs,
    operationStaffPagination,
    deleteOperationStaffResponse,
  } = useSelector((state: any) => state.assignWorker);
  const tableHeadings: TableHeading[] = [
    { label: "#", align: "left" },
    { label: "Full Name", align: "left" },
    { label: "AKA", align: "left" },
    // { label: "Mobile", align: "center" },
    { label: "Email", align: "center" },
    // { label: "Address", align: "left" },
  ];

  const [filterOption, setFilterOption] = useState<FilterOption>({
    page: 1,
    limit: 10,
    orderBy: "created_at",
    orderIn: "DESC",
    search: "",
    skill: "",
    is_deleted: "",
  });

  // Dynamic limit based on page height, debounce resize, only update if changed
  const lastLimitRef = useRef<number>(-1);
  useEffect(() => {
    let resizeTimeout: any;
    function updateLimit() {
      const pageHeight = window.innerHeight;
      const newLimit = Math.max(5, Math.floor(pageHeight / ROW_HEIGHT));
      if (lastLimitRef.current !== newLimit) {
        lastLimitRef.current = newLimit;
        setFilterOption((prev) => ({
          ...prev,
          limit: newLimit,
        }));
      }
    }
    function onResize() {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateLimit, 200);
    }
    updateLimit();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    dispatch(getAllOperationStaffAction(filterOption));
  }, [dispatch, filterOption]);

  useEffect(() => {
    dispatch(getAllSkillAction());
  }, []);

  useEffect(() => {
    if (!setSelectedId) return;

    if (operationStaffs.length === 0) {
      if (selectedId) {
        setSelectedId(null);
      }
      return;
    }

    const isSelectedStaffVisible = operationStaffs.some(
      (staff: any) => String(staff?.id) === String(selectedId)
    );

    if (!selectedId || !isSelectedStaffVisible) {
      setSelectedId(operationStaffs[0].id); // keep details in sync with current filtered list
    }
  }, [operationStaffs, selectedId, setSelectedId]);

  useEffect(() => {
    if (addOperationStaffResponse || deleteOperationStaffResponse) {
      dispatch(getAllOperationStaffAction(filterOption));
      dispatch(setAddOperationStaffAction(false));
      dispatch(setDeleteOperationStaffAction(false));
      setOpen(false);
    }
  }, [addOperationStaffResponse, deleteOperationStaffResponse]);

  const handleOpen = () => {
    setOpen(true);
  };
  // Extract vehicle types from the vehicleTypes array
  const skillsListData = skills.map((item: any) => ({
    id: item?.title,
    label: item?.title,
  }));

  const updatedFields = STAFF_FILTERS.map((field) => {
    if (field.id === "skill") {
      return { ...field, options: skillsListData, type: "select" };
    }
    return field;
  });

  const cap = (str: any) =>
    str
      ? str
          .toLowerCase()
          .split(" ")
          .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ")
      : "";

  return (
    <>
      {open && (
        <ManageStaff
          open={open}
          onHide={() => setOpen(false)}
          title="Manage Staff"
          selectedStaff={""}
          action={"add"}
          setSelectedStaff={""}
        />
      )}
      <Grid container sx={{ height: "100%" }}>
        <Grid
          item
          md={12}
          xs={12}
          id="userList"
          sx={{
            height: "100%",
            border: "1px solid #CFCFCF",
            borderRadius: "5px",
            padding: "8px",
            overflow: "hidden",
          }}
        >
          <PageTitle title="Staff" button="Add" onClick={handleOpen} />
          <Grid
            container
            spacing={1}
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 1,
              justifyContent: "space-around",
              backgroundColor: "#F9F9F9",
              padding: "0px",
              borderRadius: "5px",
            }}
          >
            {updatedFields?.map((filter, i) => (
              <FormField
                key={filter.id}
                onPaste={(e: any) => e.preventDefault()}
                {...{
                  ...filter,
                  value: filterOption ? filterOption[filter.id] : "",
                  inSingleGridForm: true,

                  variant: "outlined",

                  onChange: (e: any) => {
                    const value =
                      filter.type === "checkbox"
                        ? e.target.checked
                        : e.target.value;

                    // Clear stale details immediately when filters are changed.
                    if (setSelectedId) {
                      setSelectedId(null);
                    }

                    setFilterOption({
                      ...filterOption,
                      [filter.id]: value,
                    });
                  },

                  isNonMarginTop: true,

                  InputProps: {
                    sx:
                      filter.type === "text" || filter.type === "password"
                        ? { height: "36px" }
                        : { padding: "10px 4px", height: "36px" },
                  },
                }}
              />
            ))}
          </Grid>
          <TableDesign
            tableHeadings={tableHeadings}
            pagination={operationStaffPagination}
            filterOption={filterOption}
            stickyHeader={true}
            setFilterOption={setFilterOption}
            tableHeadColor="#E5F4F1"
            // extraCSS={{
            //   height: "calc(100% - 100px)",
            // }}
          >
            {operationStaffs.length > 0 ? (
              operationStaffs.map((val: any, idx: number) => (
                <TableRow
                  key={val?.id}
                  onClick={() => setSelectedId(val?.id)}
                  sx={{
                    cursor: "pointer",
                    marginTop: "10px",
                    padding: "4px !important",
                    borderRadius: "20px !important",
                    backgroundColor:
                      selectedId === val?.id ? "#f0f0f0" : "transparent", // ✅ active row bg
                    "&:hover": {
                      backgroundColor: "#f0f0f0",
                      outline: "2px solid #f0f0f0",
                      borderRadius: "0px !important",
                      outlineOffset: "-2px !important",
                    },
                  }}
                >
                  <TableCell align="left">{idx + 1}</TableCell>
                  <TableCell>{cap(val?.name)}</TableCell>
                  <TableCell>{cap(val?.aka_name)}</TableCell>
                  <TableCell>{val?.staff_email}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No staff found.
                </TableCell>
              </TableRow>
            )}
          </TableDesign>
        </Grid>
      </Grid>
    </>
  );
};
export default StaffList;
